import { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { FunnelIcon } from "@heroicons/react/20/solid";
import MultiSelectDropdown from "../MultiSelectDropdown";
import { Column, Filter } from "../types";

// Props para o componente FilterInput
interface FilterInputProps {
	column: Column;
	filters: Filter[];
	onUpdateFilters: (updatedFilters: Filter[]) => void;
}

export default function FilterInput({
	column,
	filters,
	onUpdateFilters,
}: FilterInputProps) {
	// Estado local para armazenar o valor atual do input
	const [inputValue, setInputValue] = useState(
		filters.find((filter) => filter.field === column.key)?.value ||
			(column.multiSelect ? [] : ""),
	);

	// Atualiza o estado local sempre que `filters` externos mudarem
	useEffect(() => {
		const currentFilterValue =
			filters.find((filter) => filter.field === column.key)?.value || "";
		setInputValue(currentFilterValue);
	}, [filters, column.key]);

	// Função debounce para aplicar o filtro no estado global após 1 segundo
	const debouncedUpdateFilters = useCallback(
		debounce((value: any) => {
			onUpdateFilters([
				...filters.filter((filter) => filter.field !== column.key),
				{
					field: column.key,
					operator: "=",
					value,
				},
			]);
		}, 1000),
		[column.key, onUpdateFilters], // Remove `filters` das dependências para evitar recriação
	);

	// Atualiza o estado local em tempo real e chama o debounce para atualizar os filtros globais
	const handleInputChange = (value: any) => {
		setInputValue(value);
		debouncedUpdateFilters(value);
	};

	const commonInputClasses =
		"w-full bg-transparent border-t-0 border-x-0 rounded-none border-b-2 border-gray-300 text-[.9rem] hover:border-amber-400 focus:border-amber-400 ring-0 pl-8";

	if (column.hidden) return null;
	// Renderização do campo de filtro com base nas opções
	if (column.filterOptions) {
		if (column.multiSelect) {
			// Dropdown com checkboxes para multiSelect
			return (
				<div className="flex items-center relative h-full">
					<FunnelIcon className="absolute w-5 h-5 text-gray-300 mr-2" />
					<MultiSelectDropdown
						options={column.filterOptions}
						value={inputValue as any}
						onChange={handleInputChange}
						placeholder="Selecione opções"
					/>
				</div>
			);
		} else {
			// Dropdown de seleção única
			return (
				<div className="flex items-center relative">
					<FunnelIcon className="absolute w-5 h-5 text-gray-300 mr-2" />
					<select
						value={inputValue as any}
						onChange={(e) => handleInputChange(e.target.value)}
						className={commonInputClasses}
					>
						<option value="" className="bg-gray-700">
							Todos
						</option>
						{column.filterOptions.map((option: any) => (
							<option
								key={option.value}
								value={option.value}
								className="bg-gray-700"
							>
								{option.label}
							</option>
						))}
					</select>
				</div>
			);
		}
	} else {
		// Campo de texto para entrada de filtros
		return (
			<div className="relative">
				<FunnelIcon className="absolute left-0 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-300" />
				<input
					type="text"
					value={inputValue as any}
					onChange={(e) => handleInputChange(e.target.value)}
					className={commonInputClasses}
				/>
			</div>
		);
	}
}
